// src/components/reports/RevenueBars.tsx — YoY grouped bar chart, hand-drawn
// SVG (dependency-free; do NOT add a chart lib). This-year brass vs last-year
// heather, by FY month; part-months hatched. Ported from the prototype.

import { C, F } from "../../theme";
import type { RevenueMonth } from "../../lib/reports";

const Legend = ({ c, label }: { c: string; label: string }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkSoft }}>
    <span style={{ width: 12, height: 12, borderRadius: 3, background: c }} />
    {label}
  </span>
);

export const RevenueBars = ({ data, h = 168 }: { data: RevenueMonth[]; h?: number }) => {
  const max = Math.max(1, ...data.flatMap((d) => [d.ty, d.ly]));
  const W = 320;
  const padB = 26;
  const padT = 8;
  const innerH = h - padB - padT;
  const groupW = W / Math.max(1, data.length);
  const barW = 16;
  const gap = 6;
  const y = (v: number) => padT + innerH - (v / max) * innerH;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${h}`} width="100%" height={h} style={{ display: "block", overflow: "visible" }}>
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1="0" x2={W} y1={y(max * g)} y2={y(max * g)} stroke={C.hairlineLt} strokeWidth="1" />
        ))}
        {data.map((d, i) => {
          const cx = i * groupW + groupW / 2;
          const x1 = cx - barW - gap / 2;
          const x2 = cx + gap / 2;
          return (
            <g key={d.m}>
              <rect x={x1} y={y(d.ly)} width={barW} height={padT + innerH - y(d.ly)} rx="3" fill={C.heatherLt} />
              <rect x={x2} y={y(d.ty)} width={barW} height={padT + innerH - y(d.ty)} rx="3" fill={C.brassDk} />
              {d.partial && (
                <rect x={x2} y={y(d.ty)} width={barW} height={padT + innerH - y(d.ty)} rx="3" fill="url(#reportsHatch)" opacity="0.5" />
              )}
              <text x={cx} y={h - 8} textAnchor="middle" fontFamily={F.body} fontSize="11" fontWeight="800" fill={C.inkMute}>
                {d.m}
              </text>
            </g>
          );
        })}
        <defs>
          <pattern id="reportsHatch" width="5" height="5" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="5" stroke={C.brassGlow} strokeWidth="2" />
          </pattern>
        </defs>
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 4, paddingLeft: 2 }}>
        <Legend c={C.brassDk} label="This year" />
        <Legend c={C.heatherLt} label="Last year" />
        <span style={{ marginLeft: "auto", fontFamily: F.serif, fontStyle: "italic", fontSize: 11, color: C.inkMute }}>
          hatched = part month
        </span>
      </div>
    </div>
  );
};
